/**
 * Test Lab Section
 *
 * Starts the development SIP Radius gateway and its TiledMedia stacks in
 * caller mode. The lab plays through its own seeded game, hidden from the
 * games list, so this is the only place it surfaces. The gateway is dialled by
 * TM rather than dialling in, so it must be up before any stack can start.
 */

import { useEffect, useRef, useState } from 'react';
import { format, parseISO } from 'date-fns';
import { Link } from 'react-router-dom';
import { post } from '@truefit/http-utils';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  FormGroup,
  Stack,
  Typography,
} from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';

import StatusBadge from '@/shared/components/StatusBadge';
import { getGameStatusColor } from '@/shared/utils/getGameStatusColor';
import { paletteHue } from '@/shared/utils/paletteHues';

import { isGatewayActive, isStackActive, useTestLabQuery } from '../hooks/useTestLabQuery';
import GatewaySignalFlow from './GatewaySignalFlow';

const GATEWAY_STATE_COLORS: Record<
  string,
  'default' | 'info' | 'success' | 'warning' | 'error'
> = {
  stopped: 'default',
  pending: 'warning',
  stopping: 'warning',
  running: 'success',
  unreachable: 'error',
  unknown: 'default',
};

// Transitional states: the box is on its way somewhere and the page is polling
// for it, so they pulse like a live game and an in-flight replay. `unreachable`
// does not — nothing is in progress, it is just broken.
const GATEWAY_IN_FLIGHT = new Set(['pending', 'stopping']);

/**
 * How long to keep Start or Stop spinning before giving up on a queued job ever
 * showing up in the data.
 *
 * Only a backstop, not the normal exit: booting and shutting the gateway down
 * each take 30-60s, and both buttons clear themselves the moment the poll sees
 * the transition. This covers the case where nothing ever arrives — a scheduler
 * that never picked the job up — so neither control spins forever with no way
 * back.
 */
const ACTION_TIMEOUT_MS = 120_000;

/**
 * Prefer the API's own message. The endpoints return a precise `error` in the
 * body — which feed was unknown, or the underlying AWS/TM failure — and Axios's
 * default message ("Request failed with status code 400") throws that away.
 */
const errorMessage = (err: unknown): string => {
  const apiError = (err as { response?: { data?: { error?: string } } })?.response
    ?.data?.error;
  return apiError || (err as Error).message || 'Unknown error';
};

export default function TestLabSection() {
  const queryClient = useQueryClient();

  /**
   * A launch has been queued but has not yet shown up in TM or EC2.
   *
   * Declared above the query because it drives the poll interval too — the
   * button and the cadence have to agree, or the spinner outlives the boot it
   * is reporting on.
   *
   * There is deliberately no equivalent for the poll on the teardown side: Stop
   * is only ever visible while the lab is active, and an active lab is already
   * on the fast cadence.
   */
  const [starting, setStarting] = useState(false);

  const { data, isLoading, error } = useTestLabQuery(starting);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [stopConfirmOpen, setStopConfirmOpen] = useState(false);
  const [selectedFeeds, setSelectedFeeds] = useState<string[]>([]);
  const [actionLoading, setActionLoading] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  /** A teardown has been queued but EC2 has not reported it yet. */
  const [stopRequested, setStopRequested] = useState(false);

  const feeds = data?.feeds ?? [];
  const game = data?.game ?? null;
  const stacks = data?.stacks ?? [];
  const gatewayState = data?.gatewayState ?? 'unknown';
  // Not `stacks.length > 0`: TM keeps returning configs whose stacks are long
  // dead, so counting entries would pin this to "Stop" forever after one run.
  const active =
    isGatewayActive(gatewayState) || stacks.some((s) => isStackActive(s.state));

  /**
   * A teardown is under way. Two sources, because neither covers the whole
   * window: the local flag spans the gap between queuing the job and EC2
   * reporting anything, and `stopping` — which comes straight from the data —
   * covers the rest, including a page reloaded mid-teardown.
   */
  const stopPending = stopRequested || gatewayState === 'stopping';

  const seededRef = useRef(false);

  // Default to every feed the mapping offers, seeded once when the first
  // response arrives. Deliberately NOT keyed on selectedFeeds.length: that would
  // re-check every box the moment the user unchecks the last one.
  useEffect(() => {
    if (!seededRef.current && feeds.length > 0) {
      seededRef.current = true;
      setSelectedFeeds(feeds.map((f) => f.feed));
    }
  }, [feeds]);

  /**
   * End the pending state once the launch is visible, or give up on it.
   *
   * `active` is the whole success condition and needs no failure counterpart: a
   * stack that comes up in `error` is still active by `isStackActive`, so a
   * failed launch clears this exactly like a good one — and lands on the Stop
   * button, which is what a failed launch needs. Watching `deploymentFailure`
   * instead would fire on the spot, since TM keeps the previous run's failure
   * on a config it is about to relaunch.
   */
  useEffect(() => {
    if (!starting) {
      return;
    }

    if (active) {
      setStarting(false);
      return;
    }

    const timer = setTimeout(() => {
      setStarting(false);
      setActionError(
        'Start was queued but the lab has not reported starting after 2 minutes.',
      );
    }, ACTION_TIMEOUT_MS);

    return () => clearTimeout(timer);
  }, [starting, active]);

  /**
   * Hand the teardown over to `gatewayState` once the data catches up.
   *
   * The condition is `!== 'running'` rather than `=== 'stopping'` because
   * `stopping` is easy to miss: EC2 passes through it, and at a 5s poll a quick
   * shutdown can read `running` then `stopped` with nothing in between. Any
   * state other than `running` means the request landed and this flag has done
   * its job — `stopPending` keeps the button spinning from `stopping` alone.
   */
  useEffect(() => {
    if (!stopRequested) {
      return;
    }

    if (gatewayState !== 'running') {
      setStopRequested(false);
      return;
    }

    const timer = setTimeout(() => {
      setStopRequested(false);
      setActionError(
        'Stop was queued but the gateway has not reported stopping after 2 minutes.',
      );
    }, ACTION_TIMEOUT_MS);

    return () => clearTimeout(timer);
  }, [stopRequested, gatewayState]);

  const handleStart = async () => {
    if (selectedFeeds.length === 0) {
      setActionError('Select at least one feed');
      return;
    }

    setActionLoading(true);
    setActionError(null);

    try {
      await post('/v1.0/test-lab/gateway/start', { feeds: selectedFeeds });
      queryClient.invalidateQueries({ queryKey: ['testLabStatus'] });
      // Only on a resolved request. A rejected queue attempt — 409 for a lab
      // already up, 400 for a bad feed — has nothing coming, and would leave the
      // button spinning for two minutes over an error already on screen.
      setStarting(true);
      setDialogOpen(false);
    } catch (err) {
      setActionError(errorMessage(err));
    } finally {
      setActionLoading(false);
    }
  };

  const handleStop = async (closeDialog?: () => void) => {
    setActionLoading(true);
    setActionError(null);

    try {
      await post('/v1.0/test-lab/gateway/stop', {});
      queryClient.invalidateQueries({ queryKey: ['testLabStatus'] });
      // Same rule as the start path: only a request that resolved has a
      // teardown behind it worth waiting on.
      setStopRequested(true);
      closeDialog?.();
    } catch (err) {
      setActionError(errorMessage(err));
    } finally {
      setActionLoading(false);
    }
  };

  return (
    <Box sx={{ mt: 5 }}>
      <Typography variant="h6" sx={{ mb: 1 }}>
        Test lab
      </Typography>
      <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>
        Start the development SIP Radius gateway and its TiledMedia stacks in caller
        mode. Push an encoder into the input port; TM dials the output port.
      </Typography>
      <Divider sx={{ marginTop: 2, marginBottom: 3 }} />

      {/* The game the lab's output is watched through. Hidden from the games
          list, so this is the only place it surfaces. Its name is not shown:
          there is only ever one lab game, so naming it says nothing the page
          has not already said. Its status does matter — that is what tells the
          player to follow a running stream rather than seek a recording. */}
      {game ? (
        <Stack
          direction="row"
          sx={{ alignItems: 'center', gap: 1, mb: 2, flexWrap: 'wrap' }}
        >
          Video : 
          {/* The same badge, hues and pulse rule as the games and Simulive
              tables: it renders the same `game.status` field, so it should not
              look like a different thing here. */}
          {game.status && (
            <StatusBadge
              label={game.status}
              color={getGameStatusColor(game.status)}
              // The lab flips recorded -> live from the scheduler once every TM
              // stack reports running, so the change arrives on a poll rather
              // than on a click. The pulse is what makes that visible.
              pulse={game.status === 'live'}
            />
          )}
          {game.startTimestamp && (
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
              started at: {format(parseISO(game.startTimestamp), 'MMM dd, yyyy HH:mm:ss')}
            </Typography>
          )}
          <Box sx={{ flexGrow: 1 }} />
          <Button
            size="small"
            component={Link}
            to={`/games/${game.uniqueId}`}
            target="_blank"
            rel="noopener noreferrer"
            variant="outlined"
          >
            Open in player
          </Button>
        </Stack>
      ) : (
        // Only once we know the answer: `game` is falsy on a cold mount and on a
        // failed fetch too, and telling the operator to run a seed script while
        // the error alert below says the API is down is the wrong instruction.
        !isLoading &&
        !error && (
          <Alert severity="info" sx={{ mb: 2 }}>
            No lab game seeded — run{' '}
            <code>doppler run -- npx tsx scripts/create-test-lab-game.ts</code> to
            create one, or lab output cannot be played.
          </Alert>
        )
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Failed to load Test lab status: {errorMessage(error)}
        </Alert>
      )}

      {actionError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {actionError}
        </Alert>
      )}

      <Stack direction="row" sx={{ alignItems: 'center', gap: 1.5, mb: 2 }}>
        Platform : 
        {isLoading ? (
          <CircularProgress size={16} />
        ) : (
          <StatusBadge
            label={gatewayState.replace(/_/g, ' ')}
            color={paletteHue(GATEWAY_STATE_COLORS[gatewayState])}
            pulse={GATEWAY_IN_FLIGHT.has(gatewayState)}
          />
        )}

        {/* The hostname, IP and instance id used to sit here too. They are the
            gateway node's own identity, so they now live on its card in the
            diagram below rather than being printed twice. */}
        <Box sx={{ flexGrow: 1 }} />

        {active ? (
          <Button
            size="small"
            variant="contained"
            color="error"
            startIcon={
              stopPending ? (
                <CircularProgress size={16} color="inherit" />
              ) : undefined
            }
            onClick={() => setStopConfirmOpen(true)}
            disabled={actionLoading || stopPending}
          >
            Stop
          </Button>
        ) : (
          <Button
            size="small"
            variant="contained"
            startIcon={
              starting ? (
                <CircularProgress size={16} color="inherit" />
              ) : undefined
            }
            onClick={() => {
              setActionError(null);
              setDialogOpen(true);
            }}
            disabled={actionLoading || starting || feeds.length === 0}
          >
            Start
          </Button>
        )}
      </Stack>

      {data?.gatewayError && (
        // The gateway read failed, but the TiledMedia half did not: the feed
        // table, stack states and Stop button below are all still valid. Warning,
        // not error — the section is usable.
        <Alert severity="warning" sx={{ mb: 2 }}>
          Gateway state could not be read: {data.gatewayError}
        </Alert>
      )}

      {feeds.length === 0 && !isLoading ? (
        <Alert severity="info">TM_PORT_MAPPING has no feeds configured</Alert>
      ) : (
        <GatewaySignalFlow
          hostname={data?.hostname ?? ''}
          gatewayState={gatewayState}
          gatewayIpAddress={data?.gatewayIpAddress ?? null}
          instanceId={data?.instanceId ?? null}
          feeds={feeds}
          stacks={stacks}
        />
      )}

      <Dialog
        open={dialogOpen}
        onClose={() => !actionLoading && setDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Start Test lab</DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <Stack spacing={2}>
            <Typography>
              Select feeds to start TiledMedia stacks for on {data?.hostname}
            </Typography>

            <FormGroup>
              {feeds.map((feed) => (
                <FormControlLabel
                  key={feed.feed}
                  control={
                    <Checkbox
                      checked={selectedFeeds.includes(feed.feed)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setSelectedFeeds([...selectedFeeds, feed.feed]);
                        } else {
                          setSelectedFeeds(
                            selectedFeeds.filter((f) => f !== feed.feed),
                          );
                        }
                      }}
                    />
                  }
                  label={`${feed.feed} — push :${feed.inputPort} → TM dials :${feed.outputPort}`}
                  sx={{ fontSize: '0.9rem' }}
                />
              ))}
            </FormGroup>

            {actionError && <Alert severity="error">{actionError}</Alert>}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)} disabled={actionLoading}>
            Cancel
          </Button>
          <Button
            onClick={handleStart}
            variant="contained"
            disabled={actionLoading || selectedFeeds.length === 0}
          >
            {actionLoading ? <CircularProgress size={20} sx={{ mr: 1 }} /> : null}
            Launch
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={stopConfirmOpen}
        onClose={() => !actionLoading && setStopConfirmOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Stop Test lab</DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <Typography>
            Stop the test lab gateway and all TiledMedia stacks on {data?.hostname}?
          </Typography>
          {actionError && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {actionError}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setStopConfirmOpen(false)} disabled={actionLoading}>
            Cancel
          </Button>
          <Button
            onClick={() => handleStop(() => setStopConfirmOpen(false))}
            variant="contained"
            color="error"
            disabled={actionLoading}
          >
            {actionLoading ? <CircularProgress size={20} sx={{ mr: 1 }} /> : null}
            Stop
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
